import { Popover } from '@headlessui/react';
import { format, parse, subYears } from 'date-fns';
import React, { useState, useEffect, ChangeEvent } from 'react';
// Relies on Date-fns for its under the hood operations.

import { FormControl } from 'react-bootstrap';
import { DayPicker, DayPickerProps } from 'react-day-picker';
import { BsCalendar as Calendar } from 'react-icons/bs';

// NOTE: Using headlessui/react and react-popper to avoid style collisions
// between react-day-picker and bootstrap.
import { usePopper } from 'react-popper';

import useDateRangePicker from './useDateRangePicker.hook';

import './BayDatepicker.scss';

export interface BayDatepickerProps extends Omit<DayPickerProps, 'mode' | 'selected' | 'onSelect' | 'fromYear' | 'toYear'> {
	dateFormat?: string;
	id?: string;
	onChangeFunc?: (date: Date, isValid: boolean, formattedValue: string) => void;
	dateValue?: Date | string | null;
	formatSymbol?: string;
	isManualEdit?: boolean;
	onHandleBlur?: (date: Date, isValid: boolean, value: string) => void;
	fromYear?: string | number;
	toYear?: string | number;
	yearAmount?: number;
	className?: string;
	placement?: 'auto' | 'auto-start' | 'auto-end' | 'top' | 'bottom' | 'right' | 'left' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'right-start' | 'right-end' | 'left-start' | 'left-end';
	strategy?: 'absolute' | 'fixed';
	emptyPlaceholder?: string | null;
}

const BayDatepicker: React.FC<BayDatepickerProps> & {
	useDateRangePicker: typeof useDateRangePicker;
} = ({
	dateFormat = 'MM/dd/yyyy',
	id,
	onChangeFunc = () => {},
	dateValue,
	formatSymbol = '/',
	isManualEdit = true,
	onHandleBlur = () => {},
	fromYear,
	toYear,
	yearAmount = 5,
	className = '',
	placement = 'bottom-start',
	strategy,
	emptyPlaceholder = null,
	...restProps
}) => {
	const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
	const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
	const { styles, attributes } = usePopper(referenceElement, popperElement, {
		placement,
		...(strategy ? { strategy } : {}),
	});
	const [value, setValue] = useState<Date | undefined>(undefined);
	const [inputValue, setInputValue] = useState('');

	useEffect(() => {
		let initialValue: Date | null = null;
		if (dateValue && !isNaN(new Date(dateValue).getTime())) {
			initialValue = new Date(dateValue);
		}
		setValue(initialValue || undefined);
		setInputValue(initialValue ? format(new Date(dateValue!), dateFormat) : '');

		return () => {
			setValue(initialValue ? new Date(dateValue!) : undefined);
			setInputValue('');
		};
	}, [dateValue, dateFormat]);

	const toYearTransformed = toYear || format(new Date(), 'yyyy');
	const fromYearTransformed =
		fromYear || format(subYears(new Date(), yearAmount), 'yyyy');

	//To Check Whether The Entered Date is between the above range
	const isValidInterval = (date: Date): boolean => {
		const fromInterval = new Date(Number(fromYearTransformed), 0, 1);
		const toInterval = new Date(Number(toYearTransformed), 11, 31);
		const selectedDate = date;
		const isValidDate =
			selectedDate >= fromInterval && selectedDate <= toInterval;
		return isValidDate;
	};

	const handleClick = (dateObject: Date) => {
		setValue(dateObject);
		setInputValue(format(dateObject, dateFormat));
		onChangeFunc(dateObject, true, format(dateObject, dateFormat));
	};

	const onHandleValidDate = (dateValue: string): boolean => {
		let isValid = false;
		const date = parse(dateValue, dateFormat, new Date());
		if (
			isNaN(new Date(date).getTime()) ||
			isNaN(new Date(dateValue).getTime()) ||
			dateValue?.length < 10
		) {
			isValid = false;
		} else if (isValidInterval(new Date(date))) {
			isValid = true;
		}
		return isValid;
	};

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		// dateformat - 'MM/dd/yyyy'
		let value = e.currentTarget.value;

		if (value?.length > 10) {
			return;
		}

		// splitFormat - ['MM,,'dd,'yyyy']
		const splitFormat = dateFormat.split(formatSymbol);

		// firstPartLength - 2 - 'MM'
		const firstPartLength = splitFormat?.[0]?.length;

		// secondPartLength - 2 - 'dd'
		const secondPartLength =
			splitFormat?.[1]?.length + splitFormat[0]?.length + 1;

		// lastCharacter of entered date
		const lastElement = value[value?.length - 1];

		// For Manually added the '/' or '-' symbols based on dateFormat
		if (
			(value?.length === firstPartLength &&
				inputValue?.length === firstPartLength - 1 &&
				lastElement !== formatSymbol) ||
			(value?.length === secondPartLength &&
				inputValue?.length === secondPartLength - 1 &&
				lastElement !== formatSymbol)
		) {
			value = lastElement === formatSymbol ? value : value + formatSymbol;
		} else if (
			(value?.length === firstPartLength + 1 &&
				inputValue?.length === firstPartLength) ||
			(value?.length === secondPartLength + 1 &&
				inputValue?.length === secondPartLength)
		) {
			value =
				lastElement === formatSymbol
					? value
					: `${inputValue + formatSymbol}${value[value?.length - 1]}`;
		} else if (value[value?.length - 1] === formatSymbol) {
			value = value.substring(0, value?.length - 1);
		}

		const date = parse(value, dateFormat, new Date());
		const isValid = onHandleValidDate(value); //To Check the Entered Date is Valid or Not
		const dateForCalendar = isValid ? new Date(date) : undefined;
		setValue(dateForCalendar); // Date value mapped into the Calendar Input Field
		onChangeFunc(new Date(date), isValid, value); // Onchange Func call for input value Type action
		setInputValue(value); // State value of Date Input Field
	};

	return (
		<Popover className="relative">
			<div
				ref={setReferenceElement}
				className={`input-group bay-datepicker ${className}`}
			>
				<FormControl
					autoComplete="off"
					className={`date-input border-end-0 ${
						!isManualEdit ? 'pe-none' : ''
					}`}
					id={id ? id : 'bay_datepicker_input'}
					placeholder={
						inputValue
							? dateFormat?.toLocaleLowerCase()
							: emptyPlaceholder || dateFormat?.toLocaleLowerCase()
					}
					type="text"
					value={inputValue}
					onBlur={(e) => {
						const date = parse(e?.target?.value, dateFormat, new Date());
						const isValid = onHandleValidDate(e?.target?.value); //To Check the Entered Date is Valid or Not
						onHandleBlur(new Date(date), isValid, e?.target?.value);
					}}
					onChange={(e) => {
						const numberPattern = /^\d+$/;
						if (
							e.currentTarget.value &&
							!numberPattern?.test(
								e.currentTarget.value?.replace(new RegExp(formatSymbol, 'g'), ''),
							)
						) {
							return;
						}
						handleInputChange(e as any);
					}}
				/>
				<Popover.Button
					as="span"
					className="input-group-text bg-white cursor-pointer rounded-1"
				>
					<Calendar />
				</Popover.Button>
			</div>
			<Popover.Panel
				ref={setPopperElement}
				style={styles.popper}
				{...attributes.popper}
				className="mt-2 bg-white shadow rounded p-1 bay-datepicker-menu"
			>
				{({ close }) => (
					<DayPicker
						fixedWeeks
						showOutsideDays
						captionLayout="dropdown-buttons"
						defaultMonth={value}
						fromYear={Number(fromYearTransformed)}
						mode="single"
						selected={value}
						toYear={Number(toYearTransformed)}
						onDayClick={() => close()}
						onSelect={(data) => {
							if (data) {
								handleClick(data);
							}
						}}
						{...restProps}
					/>
				)}
			</Popover.Panel>
		</Popover>
	);
};

BayDatepicker.useDateRangePicker = useDateRangePicker;

export default BayDatepicker;
